import React from 'react';
import { useObserver } from 'mobx-react-lite';
import styled from 'styled-components';
import tinycolor from 'tinycolor2';
import { space } from 'styled-system';

import { KIOSK_PRO_MEDIA } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-pro-shared.styles';
import useCart from 'shared/hooks/use-cart';
import CoreMenuFooter from 'dispensary/core-menu/components/core-menu-footer';
import { ConsumerInfo } from 'src/components/kiosk-pro/kiosk-pro-layout/consumer-info';
import { KioskLoginModal } from 'src/components/kiosk-pro/kiosk-login/kiosk-login-modal';
import { useKioskProLayout } from 'src/components/kiosk-pro/kiosk-pro-layout/use-kiosk-pro-layout';
import { KioskProNavigation } from 'src/components/kiosk-pro/kiosk-pro-layout/navigation';
import { EndSessionModal } from 'src/components/kiosk-pro/kiosk-pro-layout/end-session-modal';
import type { Dispensaries } from 'src/gql/graphql';

type KioskProLayoutProps = {
  dispensary: Dispensaries;
  children: React.ReactNode;
  hideCategories?: boolean;
  hideFooter?: boolean;
};

export const KioskProLayout = ({
  dispensary,
  children,
  hideCategories,
  hideFooter,
}: KioskProLayoutProps): JSX.Element => {
  const {
    isConsumerPhoneLoginPage,
    startNewSession,
    goToCart,
    kioskProModalLogin,
    createPublicContextSessionLoading,
    isKioskProLoginModalOpen,
    publicLoginSessionId,
    setIsKioskProLoginModalOpen,
    consumerPhoneLoginUrl,
    isEndSessionModalOpen,
    setIsEndSessionModalOpen,
    confirmedStartNewSession,
  } = useKioskProLayout({ dispensary });

  const Cart = useCart();
  const orderSubtotal = useObserver(() => Cart.subtotal);

  return (
    <TwoColumn padding='0'>
      <TwoColumnSidebar>
        <KioskProNavigation />
      </TwoColumnSidebar>

      <TwoColumnContent>
        {children}
        {!hideFooter && <CoreMenuFooter hideCategories={hideCategories} />}
      </TwoColumnContent>

      {!isConsumerPhoneLoginPage && (
        <ConsumerInfo
          createPublicContextSessionLoading={createPublicContextSessionLoading}
          goToCart={goToCart}
          kioskProModalLogin={kioskProModalLogin}
          orderSubTotal={orderSubtotal}
          startNewSession={startNewSession}
        />
      )}

      {isKioskProLoginModalOpen && !!publicLoginSessionId && (
        <KioskLoginModal
          isOpen={isKioskProLoginModalOpen}
          handleClose={() => setIsKioskProLoginModalOpen(false)}
          consumerPhoneLoginUrl={consumerPhoneLoginUrl}
        />
      )}

      <EndSessionModal
        startNewSession={() => void confirmedStartNewSession()}
        isOpen={isEndSessionModalOpen}
        handleCloseModal={() => setIsEndSessionModalOpen(false)}
      />
    </TwoColumn>
  );
};

const TwoColumn = styled.div`
  display: flex;
  padding: 0;

  ${space}

  a, button {
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    user-select: none;
  }

  @media (orientation: portrait) {
    flex-direction: column;
  }
`;

const TwoColumnSidebar = styled.div`
  width: 400px;
  background-color: ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 90).toString()};

  @media ${KIOSK_PRO_MEDIA.maxWidth} {
    width: 320px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    width: 280px;
  }

  @media (orientation: portrait) {
    width: 100%;
  }
`;

export const TwoColumnContent = styled.div`
  padding: 10px;
  flex: 1 0 0%;
  min-width: 0;
  min-height: 100vh;
  --kiosk-pro-content-width: 100%;

  .two-column-content {
    padding: 26px 0 0;
  }

  .embla__viewport {
    width: calc(100% + 52px);
    margin-left: -26px;

    @media (min-width: 770px) {
      width: 100%;
      margin-left: 0;
    }
  }

  @media (orientation: portrait) {
    padding-bottom: 300px;
    padding-top: 250px;
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    padding-bottom: 280px;
    padding-top: 210px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait} {
    padding-bottom: 200px;
    padding-top: 180px;
  }
`;
